
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { PDFViewer, pdf } from "@react-pdf/renderer";

import Sidebar from "../../../DesignSystem/Sidebar";
import "../../../../css/AML/CheckDetails/CheckDetails.css";

import { Spinner } from "../Components/Spinner/spinner";
import SecondaryButton from "../Components/Buttons/SecondaryButton";
import Banner from "../Components/Banners/Banner";
import AMLInfoTooltip from "../Components/Tooltip/AMLInfoTooltip";

import { ReactComponent as ExportIcon } from "../../../../assets/icons/print.svg";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/aml/information.svg";
import { ReactComponent as CopyIcon } from "../../../../assets/icons/aml/Copy.svg";

import AMLReport from "../Report/AMLReport";
import IDVerification from "./HelperFunctions/getIDVerification";
import DatabaseVerification from "./HelperFunctions/getDatabaseVerification";
import PEPSanctions from "./HelperFunctions/getPEPSanctions";
// import ExperianIDVVerify from "./HelperFunctions/getExperianContras";

import {
  isCheck2Admin,
  canDownloadAmlReport,
} from "../Utils/hasAdminPermissions";

import {
  fetchSessionMedia,
  fetchAttemptMedia,
  isSelfie,
} from "./HelperFunctions/helperFunctions";

import { TYPE_MAP, API_BASE } from "./Utils/getConstants";
import { isVeriffSession } from "./Utils/isVeriffSession";
import { formatCheckType } from "./Utils/formatCheckType";

import experianServiceNames from "./Utils/Experian/getExperianServiceNames";
import { subscribePepSanReasonsByRef } from "./Utils/Experian/pepSanReasonsStore";
import { subscribeExperianResponse as subscribeExperianResponseLatest } from "./Utils/Experian/subscribeExperianResponse";
import { fetchExperianResponseLatest } from "./Utils/Experian/fetchExperianResponseLatest";

import { getAllMatchesResolved } from "./Utils/Experian/allMatchesHaveReasons";
// import { canAccessAML } from "../Utils/permissions";
import { useUserEmail } from "../../../../context/UserEmailContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import { useUserPermissions } from "../../../../context/UserPermissionsContext";

import { computeOverallOutcome } from "./Utils/outcome/checkOutcome";
import CheckOutcome from "./HelperFunctions/getCheckOutcome";
import formatAddress from "../Checks/utils/factories/formatAddress";
import { useCheck2GuideUrl } from "../Checks/utils/guide/getUrl";
import { isVeryDifferent } from "./Utils/verifyName";
import { isSipOnlySanction } from "./Utils/Experian/pepSanCards";

// ---------------- helpers ----------------
const norm = (v) => (v ?? "").toString().trim().toLowerCase();
const unwrap = (x) => x?.response ?? x ?? null;

const pepAliases = Array.isArray(experianServiceNames.PEP_SANCTIONS)
  ? experianServiceNames.PEP_SANCTIONS
  : [experianServiceNames.PEP_SANCTIONS];

const getPepElement = (experianWrapperOrInner) => {
  const r = unwrap(experianWrapperOrInner);
  const els = r?.clientResponsePayload?.decisionElements || [];
  return els.find((d) =>
    pepAliases.some((s) => norm(s) === norm(d?.serviceName)),
  );
};

const hasPepSanMatches = (experianWrapperOrInner) => {
  return buildPepSanCardRefs(experianWrapperOrInner).length > 0;
};

const buildPepSanCardRefs = (experianWrapperOrInner) => {
  const el = getPepElement(experianWrapperOrInner);
  const results = el?.otherData?.response?.searchResults?.[0]?.results || [];

  const counters = {};
  const cards = [];

  results.forEach((match) => {
    const types = Array.isArray(match?.matchingCriteria)
      ? [
          ...new Set(
            match.matchingCriteria.map((c) =>
              String(c?.searchType || "").toUpperCase(),
            ),
          ),
        ]
      : [];

    types.forEach((t) => {
      if (isSipOnlySanction(match, t)) return;
      counters[t] = (counters[t] || 0) + 1;
      cards.push({ ref: `${t || "UNK"}${counters[t] || 1}` });
    });
  });

  return cards;
};

const getReasonsForRef = (reasonsByRefArg, ref) => {
  const entry = reasonsByRefArg?.[ref];
  const arr = Array.isArray(entry)
    ? entry
    : Array.isArray(entry?.reasons)
      ? entry.reasons
      : [];
  return (arr || []).map((x) => String(x || "").trim()).filter(Boolean);
};

const hasRcaMatches = (experianWrapperOrInner) => {
  const r = unwrap(experianWrapperOrInner);
  const el = getPepElement(r);
  const results = el?.otherData?.response?.searchResults?.[0]?.results || [];

  return results.some((res) =>
    (res?.matchingCriteria || []).some(
      (c) => String(c?.searchType || "").toUpperCase() === "RCA",
    ),
  );
};

export default function CheckResult() {
  const { state } = useLocation();
  const navigate = useNavigate();

  const {
    checkType,
    sessionId,
    vendorData,
    ownerEmail,
    name,
    sessionUrl,
    forAdviser,
    experianResponse, // initial response (maybe stale)
    createdBy,
    reasonsByRef, // initial reasons (maybe stale)
    createdAt,
    fullAddress,
  } = state || {};

  const lookupOwnerEmail = ownerEmail || createdBy;

  const dob = state?.dob
    ? dayjs(state.dob, ["DD/MM/YYYY", dayjs.ISO_8601], true)
    : null;

  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const lastFetchedKeyRef = useRef(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [refreshArmed, setRefreshArmed] = useState(false);

  const [downloadAttempted, setDownloadAttempted] = useState(false);
  const [pepExportAllowed, setPepExportAllowed] = useState(true);
  const [pepExportMsg, setPepExportMsg] = useState("");
  const [rcaExportMsg, setRcaExportMsg] = useState("");

  const { userEmail } = useUserEmail();
  const { userRole } = useUserRole();
  const { userPermissions } = useUserPermissions();

  const check2GuideUrl = useCheck2GuideUrl();

  const isAdmin = useMemo(
    () => isCheck2Admin(userRole, userPermissions),
    [userRole, userPermissions],
  );

  // console.log(state);

  // -------- endpoint --------
  const endpoint = useMemo(() => {
    if (!checkType || !sessionId) return "";
    const t = (checkType || "").toLowerCase();
    const base = process.env.REACT_APP_AML_SERVER || "";
    return t === "enhanced"
      ? `${base}get-idv-decision/${encodeURIComponent(sessionId)}`
      : `${base}get-docOnly-decision/${encodeURIComponent(sessionId)}`;
  }, [checkType, sessionId]);

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(sessionUrl);
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 1500); // disappears after 1.5s
  };

  // ---------------- access gate ----------------
  const [hasAmlAccess, setHasAmlAccess] = useState(false);
  const [amlAccessChecked, setAmlAccessChecked] = useState(false);

  // useEffect(() => {
  //   let mounted = true;

  //   (async () => {
  //     const allowed = canAccessAML({
  //       userRole,
  //       userPermissions,
  //       userEmail,
  //     });

  //     if (!mounted) return;

  //     setHasAmlAccess(allowed);
  //     setAmlAccessChecked(true);
  //   })();

  //   return () => {
  //     mounted = false;
  //   };
  // }, [userRole, userPermissions, userEmail]);

  // useEffect(() => {
  //   if (!amlAccessChecked) return;
  //   if (!hasAmlAccess) navigate("/MembersHub", { replace: true });
  // }, [amlAccessChecked, hasAmlAccess, navigate]);

  // ---------------- load check ----------------
  const loadCheck = useCallback(
    async (force = false) => {
      if (!API_BASE || !checkType || !sessionId) return;

      const ep = endpoint;
      if (!force && lastFetchedKeyRef.current === ep) return;
      lastFetchedKeyRef.current = ep;

      const controller = new AbortController();
      const { signal } = controller;

      const makeJson = async (res) => {
        if (!res.ok) {
          const t = await res.text();
          throw new Error(`HTTP ${res.status}: ${t.slice(0, 200)}`);
        }
        const ct = res.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          const t = await res.text();
          throw new Error(
            `Expected JSON, got ${ct}. Snippet: ${t.slice(0, 200)}`,
          );
        }
        return res.json();
      };

      const fetchMediaFile = async (type, fid) => {
        try {
          const res = await fetch(`${API_BASE}media/${type}/${fid}`, {
            signal,
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const base64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          return { fid, base64 };
        } catch {
          return null;
        }
      };

      try {
        setLoading(true);
        setErr("");
        setCheck(null);

        const tLower = (checkType || "").toLowerCase();
        const type = TYPE_MAP[tLower];
        if (!type) throw new Error(`Unsupported checkType: ${checkType}`);

        const decisionUrl =
          tLower === "enhanced"
            ? `${API_BASE}get-idv-decision/${encodeURIComponent(sessionId)}`
            : `${API_BASE}get-docOnly-decision/${encodeURIComponent(
                sessionId,
              )}`;

        const attemptsUrl = `${API_BASE}get-attempts/${type}/${encodeURIComponent(
          sessionId,
        )}`;

        const [decisionRes, attemptsRes] = await Promise.all([
          fetch(decisionUrl, { signal }),
          fetch(attemptsUrl, { signal }),
        ]);

        const [decisionJson, attemptsJson] = await Promise.all([
          makeJson(decisionRes),
          makeJson(attemptsRes),
        ]);

        const combinedBase = {
          ...decisionJson,
          attempts: attemptsJson?.attempts ?? attemptsJson,
        };

        if (signal.aborted) return;

        const sessionMediaPromise = fetchSessionMedia(
          API_BASE,
          type,
          sessionId,
          signal,
        ).catch(() => null);

        const attemptsArr =
          combinedBase?.attempts?.verifications ??
          combinedBase?.attempts?.attempts ??
          (Array.isArray(combinedBase?.attempts)
            ? combinedBase.attempts
            : []) ??
          [];

        const attemptMediaPromises = attemptsArr.map((att) => {
          const attemptId = att?.attemptId || att?.id;
          if (!attemptId) return null;
          return fetchAttemptMedia(API_BASE, type, attemptId, signal).catch(
            () => null,
          );
        });

        const [sessionMedia, attemptMediaList] = await Promise.all([
          sessionMediaPromise,
          Promise.all(attemptMediaPromises),
        ]);

        const attemptsWithMeta = attemptsArr.map((att, idx) => ({
          ...att,
          media: attemptMediaList[idx],
        }));

        // collect media ids
        const mediaIds = [];
        if (sessionMedia?.images)
          mediaIds.push(...sessionMedia.images.map((i) => i.id));
        if (sessionMedia?.videos)
          mediaIds.push(...sessionMedia.videos.map((v) => v.id));
        for (const att of attemptsWithMeta) {
          if (att?.media?.images)
            mediaIds.push(...att.media.images.map((i) => i.id));
          if (att?.media?.videos)
            mediaIds.push(...att.media.videos.map((v) => v.id));
        }

        const fileResults = await Promise.all(
          mediaIds.map((fid) => fetchMediaFile(type, fid)),
        );
        const filesById = Object.fromEntries(
          fileResults.filter(Boolean).map((f) => [f.fid, f.base64]),
        );

        const attachBase64 = (mediaObj) => {
          if (!mediaObj) return mediaObj;
          const patch = (list) =>
            Array.isArray(list)
              ? list.map((i) =>
                  filesById[i.id] ? { ...i, base64: filesById[i.id] } : i,
                )
              : list;
          return {
            ...mediaObj,
            images: patch(mediaObj.images),
            videos: patch(mediaObj.videos),
          };
        };

        const combined = {
          ...combinedBase,
          sessionMedia: attachBase64(sessionMedia),
          attempts: attemptsWithMeta.map((att) => ({
            ...att,
            media: attachBase64(att.media),
          })),
        };

        setCheck(combined);
      } catch (e) {
        setErr(e?.message || "Failed to load");
      } finally {
        setLoading(false);
      }

      return () => controller.abort();
    },
    [checkType, sessionId, endpoint],
  );

  useEffect(() => {
    loadCheck(true);
  }, [loadCheck]);

  // ---------------- derived lists ----------------
  const attemptList =
    check?.attempts?.verifications ??
    check?.attempts?.attempts ??
    (Array.isArray(check?.attempts) ? check.attempts : []) ??
    [];

  const attemptsSorted = useMemo(() => {
    const arr =
      attemptList?.slice?.() ??
      (Array.isArray(attemptList) ? [...attemptList] : []);
    return arr.sort(
      (a, b) => new Date(b?.createdTime || 0) - new Date(a?.createdTime || 0),
    );
  }, [attemptList]);

  // Check if the latest attempt (first in sorted array) has videos
  const latestAttemptHasVideo = useMemo(() => {
    const latestAttempt = attemptsSorted[0];
    return latestAttempt?.media?.videos?.length > 0;
  }, [attemptsSorted]);

  // ---------------- banner auto-clear ----------------
  useEffect(() => {
    if (!downloadAttempted || !pepExportMsg) return;
    const t = setTimeout(() => setPepExportMsg(""), 10000);
    return () => clearTimeout(t);
  }, [pepExportMsg, downloadAttempted]);

  useEffect(() => {
    // if (!downloadAttempted || !rcaExportMsg) return;
    const t = setTimeout(() => setRcaExportMsg(""), 10000);
    return () => clearTimeout(t);
  }, [rcaExportMsg, downloadAttempted]);

  // ---------------- refresh after popup ----------------
  useEffect(() => {
    if (!refreshArmed) return;
    if (!sessionId || !checkType) return;

    const timer = setTimeout(async () => {
      await loadCheck(true);
      setRefreshArmed(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [refreshArmed, loadCheck, sessionId, checkType]);

  // ---------------- LIVE subscriptions (Experian + Reasons) ----------------
  const [liveExperianResponse, setLiveExperianResponse] = useState(null);
  const [liveReasonsByRef, setLiveReasonsByRef] = useState({});

  useEffect(() => {
    if (!lookupOwnerEmail || !vendorData) return;

    let unsubExp = null;
    let unsubReasons = null;
    let cancelled = false;

    // Experian latest
    try {
      unsubExp = subscribeExperianResponseLatest(
        lookupOwnerEmail,
        vendorData,
        (nextDoc) => {
          if (cancelled) return;
          const exp = nextDoc?.response ?? nextDoc ?? null;
          setLiveExperianResponse(exp);
        },
        () => {
          if (!cancelled) setLiveExperianResponse(null);
        },
      );
    } catch {
      setLiveExperianResponse(null);
    }

    // Reasons latest
    (async () => {
      try {
        unsubReasons = await subscribePepSanReasonsByRef(
          lookupOwnerEmail,
          vendorData,
          (next) => {
            if (cancelled) return;
            setLiveReasonsByRef(next || {});
          },
        );
      } catch {
        if (!cancelled) setLiveReasonsByRef({});
      }
    })();

    return () => {
      cancelled = true;
      if (typeof unsubExp === "function") unsubExp();
      if (typeof unsubReasons === "function") unsubReasons();
    };
  }, [lookupOwnerEmail, vendorData]);

  // Choose best response for UI: live > state
  const experianForUI = useMemo(
    () => liveExperianResponse || unwrap(experianResponse),
    [liveExperianResponse, experianResponse],
  );

  const reasonsForUI = useMemo(
    () => liveReasonsByRef || reasonsByRef || {},
    [liveReasonsByRef, reasonsByRef],
  );

  const pepSanHasMatches = useMemo(
    () => hasPepSanMatches(experianForUI),
    [experianForUI],
  );

  const rcaPresent = useMemo(
    () => hasRcaMatches(experianForUI),
    [experianForUI],
  );

  // LIVE outcome
  const outcomeLive = useMemo(() => {
    return computeOverallOutcome({
      rowOrCheck: check,
      checkType,
      experianResponse: experianForUI,
      reasonsByRef: reasonsForUI,
      getAllMatchesResolved,
    });
  }, [check, checkType, experianForUI, reasonsForUI]);

  // ---------------- export gating (use LIVE reasons) ----------------
  useEffect(() => {
    if (!downloadAttempted) return;

    if (isAdmin) {
      setPepExportAllowed(true);
      setPepExportMsg("");
      return;
    }

    // If no PEP matches, allow
    if (!pepSanHasMatches) {
      setPepExportAllowed(true);
      setPepExportMsg("");
      return;
    }

    // Need cards + reasons; both are already live
    try {
      const cards = buildPepSanCardRefs(experianForUI);
      const resolved = getAllMatchesResolved(cards, (ref) =>
        getReasonsForRef(reasonsForUI, ref),
      );

      setPepExportAllowed(resolved);
      setPepExportMsg(
        resolved
          ? ""
          : "Please confirm all PEP & Sanctions matches before downloading the report.",
      );
    } catch {
      setPepExportAllowed(false);
      setPepExportMsg(
        "Please confirm all PEP & Sanctions matches before downloading the report.",
      );
    }
  }, [
    downloadAttempted,
    isAdmin,
    pepSanHasMatches,
    experianForUI,
    reasonsForUI,
  ]);

  const downloadDisabled = useMemo(() => {
    if (isAdmin) return true;

    return (
      outcomeLive?.checkResult !== "Expired" ||
      outcomeLive?.checkResult !== "Abandoned"
    );
    // return outcomeLive?.checkResult !== "Action Required" && outcomeLive?.checkResult !== "Expired";

    const pepBlocked =
      downloadAttempted && pepSanHasMatches && !pepExportAllowed;

    return !canDownloadAmlReport({
      userRole,
      userPermissions,
      rcaPresent,
      pepBlocked,
    });
  }, [
    isAdmin,
    userRole,
    userPermissions,
    rcaPresent,
    downloadAttempted,
    pepSanHasMatches,
    pepExportAllowed,
    outcomeLive,
  ]);

  const verifiedName = check?.verification?.person?.firstName
    ? `${check?.verification?.person?.firstName} ${check?.verification?.person?.lastName}`
    : "";

  const showNameWarning = isVeryDifferent(name, verifiedName);

  // ---------------- UI ----------------
  return (
    <div className="Main-Page">
      <Helmet>
        <title>
          {`${
            check?.verification?.person?.firstName
              ? [
                  check?.verification?.person?.firstName,
                  check?.verification?.person?.lastName,
                ]
                  .filter(Boolean)
                  .join(" ")
              : name
          } • check² result`}
        </title>
      </Helmet>

      <Sidebar />

      <div className="Check-Result-Container">
        {loading && (
          <div className="loader-overlay">
            <Spinner label="Fetching check details…" />
          </div>
        )}

        {/* {!!err && <Banner type="error" message={err} />} */}

        <div className="Check-Result-Header ">
          <div className="AML-Header-Row1">
            <div className="AML-Text-Subtitle">RESULT</div>
            <div className="AML-Header-Row Gap">
              <div className="AML-Text-Body">Data Partners</div>
              <div className="AML-Header-Row">
                <div className="AML-Text-Input">Veriff</div>
                <div className="AML-Text-Input">•</div>
                <div className="AML-Text-Input">Experian</div>
              </div>
            </div>
          </div>

          {downloadDisabled && (
            <SecondaryButton
              icon={<ExportIcon />}
              label="Download"
              disabled={downloadDisabled}
              onClick={async () => {
                setDownloadAttempted(true);

                try {
                  setLoading(true);
                  setPepExportMsg("");
                  setRcaExportMsg("");

                  // Always fetch freshest Experian for PDF
                  const liveExperianDoc = await fetchExperianResponseLatest(
                    lookupOwnerEmail,
                    vendorData,
                  );
                  const expForPdf =
                    liveExperianDoc?.response ??
                    liveExperianDoc ??
                    state?.experianResponse?.response ??
                    {};
                  const reportOutcome = computeOverallOutcome({
                    rowOrCheck: check,
                    checkType,
                    experianResponse: expForPdf,
                    reasonsByRef: reasonsForUI,
                    getAllMatchesResolved,
                  });
                  const reportPepCards = buildPepSanCardRefs(expForPdf);
                  const reportPepResolved = getAllMatchesResolved(
                    reportPepCards,
                    (ref) => getReasonsForRef(reasonsForUI, ref),
                  );

                  // RCA gate (admins bypass)
                  // const liveHasRca = hasRcaMatches(expForPdf);
                  // if (!isAdmin && liveHasRca) {
                  //   setRcaExportMsg(
                  //     "RCA has been detected. Downloading the report is disabled. Please refer the case to Compliance.",
                  //   );
                  //   return;
                  // }

                  // PEP gate (admins bypass) - use LIVE reasons (already subscribed)
                  if (
                    !isAdmin &&
                    reportPepCards.length > 0 &&
                    !reportPepResolved
                  ) {
                    setPepExportMsg(
                      "Please confirm all PEP & Sanctions matches before downloading the report.",
                    );
                    return;
                  }

                  const blob = await pdf(
                    <AMLReport
                      check={check}
                      checkType={checkType}
                      state={state}
                      pepSanReasonsByRef={reasonsForUI}
                      experianResponseLive={expForPdf}
                      portrait={
                        attemptsSorted[0]?.media?.images?.find(isSelfie)?.base64
                      }
                      docFront={
                        attemptsSorted[0]?.media?.images?.find(
                          (img) => img.context === "document-front",
                        )?.base64
                      }
                      docBack={
                        attemptsSorted[0]?.media?.images?.find(
                          (img) => img.context === "document-back",
                        )?.base64
                      }
                      overallOutcome={reportOutcome?.checkResult || ""}
                    />,
                  ).toBlob();

                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${
                    check?.verification?.person?.firstName
                      ? `${check.verification.person.firstName} ${check.verification.person.lastName}`
                      : name
                  } - ${dayjs().format("DD MMM YYYY")}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                } finally {
                  setLoading(false);
                }
              }}
            />
          )}
        </div>

        <div className="Check-Details-Section">
          {!!pepExportMsg && pepSanHasMatches && downloadAttempted && (
            <Banner type="warning" message={pepExportMsg} />
          )}
          {/* {!!rcaExportMsg && downloadAttempted && (
            <Banner type="warning" message={rcaExportMsg} />
          )} */}

          {showPDFModal && (
            <PDFViewer style={{ width: "100%", height: "85vh" }} height={1200}>
              <AMLReport
                check={check}
                checkType={checkType}
                state={state}
                pepSanReasonsByRef={reasonsForUI}
                experianResponseLive={experianForUI}
                portrait={
                  attemptsSorted[0]?.media?.images?.find((img) => isSelfie(img))
                    ?.base64
                }
                docFront={
                  attemptsSorted[0]?.media?.images?.find(
                    (img) => img.context === "document-front",
                  )?.base64
                }
                docBack={
                  attemptsSorted[0]?.media?.images?.find(
                    (img) => img.context === "document-back",
                  )?.base64
                }
                overallOutcome={outcomeLive?.checkResult || ""}
              />
            </PDFViewer>
          )}

          <div className="Check-Details-Section-Row">
            <div className="Check-Details-Section-Column">
              <div className="AML-Text-Subtitle">
                {/* {check?.verification?.person?.firstName ? (
                  <>
                    {check?.verification?.person?.firstName}{" "}
                    {check?.verification?.person?.lastName}
                  </>
                ) : (
                  name
                )} */}
                {name || ""}
              </div>

              <div className="Check-Details-Section-Row">
                <div className="Check-Details-Section-Column">
                  <div className="Text-Column">
                    <div className="AML-Text-Body">DATE OF BIRTH</div>
                    <div className="AML-Text-Input">
                      {dob && dayjs(dob).isValid()
                        ? dayjs(dob).format("DD MMM YYYY")
                        : ""}
                    </div>
                  </div>

                  <div className="Text-Column" style={{ maxWidth: "200px" }}>
                    <div className="AML-Text-Body">ADDRESS</div>
                    <div className="AML-Text-Input">
                      {formatAddress(fullAddress) || "-"}
                    </div>
                  </div>
                </div>{" "}
                <div className="divider-vertical" />
                <div className="Check-Details-Section-Column">
                  <div className="Text-Column">
                    <div className="AML-Text-Body">CHECK TYPE</div>
                    <div className="AML-Text-Input">
                      {formatCheckType(checkType) || "-"}
                    </div>
                  </div>

                  <div className="Text-Column">
                    <div className="AML-Text-Body">DATE RUN</div>
                    <div className="AML-Text-Input">
                      {createdAt && dayjs(createdAt).isValid()
                        ? dayjs(createdAt).format("DD MMM YYYY")
                        : "-"}
                    </div>
                  </div>

                  <div className="Text-Column">
                    <AMLInfoTooltip
                      text="You can view this check before the stated deadline. Once the deadline has passed, data cannot be recovered."
                      position="top"
                    >
                      <div className="AML-Text-Body">DATA RETENTION</div>
                      <InfoIcon className="InfoIcon" />
                    </AMLInfoTooltip>
                    <div className="AML-Text-Input">{state?.expiration}</div>
                  </div>
                </div>
                <div className="divider-vertical" />
                <div className="Check-Details-Section-Column">
                  <div className="Text-Column">
                    <div className="AML-Text-Body">VENDOR DATA</div>
                    <div className="AML-Text-Input">{vendorData || "-"}</div>
                  </div>

                  {check?.verification?.decisionTime && (
                    <div className="Text-Column">
                      <div className="AML-Text-Body">LAST UPDATE</div>
                      <div className="AML-Text-Input">
                        {dayjs(check.verification.decisionTime).isValid()
                          ? dayjs(check.verification.decisionTime).format(
                              "DD MMM YYYY",
                            )
                          : "-"}
                      </div>
                    </div>
                  )}

                  {(check?.verification?.status?.toLowerCase() ===
                    "resubmission_requested" ||
                    !check?.verification?.status) &&
                    isVeriffSession(checkType) && (
                      <div className="Text-Column">
                        <AMLInfoTooltip
                          text="You can follow this link to complete or review the session."
                          position="top"
                        >
                          <div className="AML-Text-Body">SESSION URL</div>
                          <InfoIcon className="InfoIcon" />
                        </AMLInfoTooltip>

                        {sessionUrl ? (
                          <div className="Session-Icon-Row">
                            <a
                              className="AML-Text-Input"
                              style={{ cursor: "pointer" }}
                              onClick={handleCopy}
                            >
                              Copy link
                            </a>

                            <div className="TooltipWrapper-Right">
                              <CopyIcon
                                onClick={handleCopy}
                                className="CopyIcon"
                                tabIndex={-1}
                                focusable="false"
                              />
                            </div>
                            {copied && (
                              <div className="CopiedPopup AML-Text-Body">
                                Copied!
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="AML-Text-Input">No session found</div>
                        )}
                      </div>
                    )}
                </div>
                <div className="divider-vertical" />
                <div className="Check-Details-Section-Column">
                  {latestAttemptHasVideo && (
                    <div className="Text-Column">
                      <div className="AML-Text-Body">ID VERIFICATION</div>
                      <div className="AML-Text-Input">
                        Completed by the client
                      </div>
                    </div>
                  )}{" "}
                  <div className="Text-Column">
                    <div className="AML-Text-Body">check² GUIDE</div>
                    <div className="AML-Text-Input">
                      <a
                        className="AML-Text-Input"
                        style={{
                          gap: "10px",
                          cursor: check2GuideUrl ? "pointer" : "not-allowed",
                          opacity: check2GuideUrl ? 1 : 0.5,
                        }}
                        onClick={() => {
                          if (!check2GuideUrl) return;
                          window.open(
                            check2GuideUrl,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                      >
                        View
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* {latestAttemptHasVideo && (
            <Banner
              type="info"
              message="The latest attempt was completed by the client."
            />
          )} */}
          {showNameWarning && (
            <Banner
              type="warning"
              message={
                "Please note that the name provided does not match the name shown on the document."
              }
            />
          )}
          {forAdviser && !isAdmin && (
            <Banner
              type="info"
              message={`This check was requested on behalf of ${forAdviser}.`}
            />
          )}
          {isAdmin && (
            <Banner
              type="info"
              message={`This check was requested by ${check?.createdBy || createdBy || "unknown"}${forAdviser ? ` on behalf of ${forAdviser}` : ""}.`}
            />
          )}

          {/* {check?.verification?.reason && (
            <Banner type="error" message={check.verification.reason} />
          )} */}

          {!!experianForUI && (
            <CheckOutcome
              check={check}
              experianResponse={experianForUI}
              outcome={outcomeLive}
              checkType={checkType}
              ownerEmail={check?.createdBy || createdBy}
              checkId={vendorData}
            />
          )}

          <IDVerification
            check={check}
            loadCheck={loadCheck}
            armRefresh={() => setRefreshArmed(true)}
          />

          {!!experianForUI && (
            <>
              <DatabaseVerification
                checkId={vendorData}
                ownerEmail={lookupOwnerEmail}
                check={check}
                checkType={checkType}
                response={experianForUI}
                serviceName={experianServiceNames.ID_VERIFICATION}
              />

              <PEPSanctions
                serviceName={experianServiceNames.PEP_SANCTIONS}
                reasonsByRef={reasonsForUI}
                response={experianForUI}
                checkId={vendorData}
                ownerEmail={lookupOwnerEmail}
                dob={dob}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
